/* VariantAuthoringTab.js — v6.32.161

   Super Admin → Operations → Variant Authoring.

   Flow:
     1) Operator picks a parent question via grade + modality filter +
        free-text search.
     2) Operator picks an accommodation type (RA, ES, WD, HL).
     3) For WD, operator picks which of the 5 NYC-top languages to draft.
     4) Click [Generate Draft] → backend calls MOSAIC content engine → draft
        override JSON returns.
     5) Operator reviews + edits the draft in place.
     6) Click [Save Variant] → POSTs to the v6.32.151
        /api/super-admin/question-variants/ endpoint.

   All MOSAIC content-engine budget tracking + audit logging happens server-side.
   This tab is read-only against db.questions and write-only against
   db.question_variants via the existing v6.32.151 save endpoint.
*/

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpen, Clock, Highlighter as HLIcon, Volume2,
  Search, Sparkles, Save, Loader2, CheckCircle, AlertTriangle,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

const GRADES = ["K", "1", "2", "3-4", "5-6", "7-8", "9-12"];
const MODALITIES = ["listening", "speaking", "reading", "writing"];
const PRODUCT_LINES = ["summative", "practice"];
const WD_LANGS = [
  { code: "es", label: "Spanish" },
  { code: "zh", label: "Mandarin" },
  { code: "ar", label: "Arabic" },
  { code: "ru", label: "Russian" },
  { code: "ht", label: "Haitian Creole" },
];

const ACCOMS = [
  { code: "RA", label: "Read-Aloud Script", icon: Volume2,    color: "amber"   },
  { code: "WD", label: "WD Glossary",       icon: BookOpen,   color: "emerald" },
  { code: "ES", label: "Extended Time",     icon: Clock,      color: "blue"    },
  { code: "HL", label: "Highlighter",       icon: HLIcon,     color: "yellow"  },
];

// ---------------- subcomponents ----------------

function QuestionPicker({ onPick }) {
  const [grade, setGrade] = useState("K");
  const [modality, setModality] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (grade) params.set("grade", grade);
      if (modality) params.set("modality", modality);
      if (q) params.set("q", q);
      const res = await axios.get(
        `${API}/api/super-admin/variant-authoring/questions?${params.toString()}`
      );
      setRows(res.data.rows || []);
    } catch (e) {
      toast.error("Failed to load questions: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  // initial load
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="bg-white border border-gray-200 p-4" data-testid="variant-picker-card">
      <div className="flex items-center gap-2 mb-3">
        <Search size={16} className="text-gray-600" />
        <h3 className="text-sm font-bold text-gray-900">Pick a Parent Question</h3>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <select
          value={grade}
          onChange={(e) => setGrade(e.target.value)}
          className="h-9 border border-gray-300 px-2 text-sm"
          data-testid="variant-picker-grade"
        >
          <option value="">All grades</option>
          {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select
          value={modality}
          onChange={(e) => setModality(e.target.value)}
          className="h-9 border border-gray-300 px-2 text-sm"
          data-testid="variant-picker-modality"
        >
          <option value="">All modalities</option>
          {MODALITIES.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <Input
          placeholder="Search question text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchRows()}
          className="h-9 text-sm"
          data-testid="variant-picker-q"
        />
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={fetchRows}
        disabled={loading}
        className="h-8 mb-3"
        data-testid="variant-picker-search"
      >
        {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Search size={14} className="mr-1" />}
        Search
      </Button>
      <div className="max-h-72 overflow-y-auto border border-gray-100">
        {rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 italic">No matches.</div>
        ) : (
          <ul data-testid="variant-picker-results">
            {rows.map((r) => (
              <li
                key={r.id}
                className="border-b border-gray-100 last:border-b-0 hover:bg-emerald-50 cursor-pointer p-2"
                onClick={() => onPick(r)}
                data-testid={`variant-picker-row-${r.id}`}
              >
                <div className="text-xs text-gray-500 font-mono">{r.grade_level} · {r.modality}</div>
                <div className="text-sm text-gray-900 line-clamp-2">{r.question_text}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ParentPreview({ parent, onClear }) {
  return (
    <div className="bg-gray-50 border border-gray-200 p-3 mb-3" data-testid="variant-parent-preview">
      <div className="flex justify-between items-start mb-1">
        <span className="text-[10px] font-bold tracking-wide text-gray-500 uppercase">Parent question</span>
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-gray-500 hover:text-gray-800"
          data-testid="variant-parent-clear"
        >
          Change
        </button>
      </div>
      <div className="text-xs font-mono text-gray-500 mb-1">{parent.id}</div>
      <div className="text-xs text-gray-600">{parent.grade_level} · {parent.modality}</div>
      <div className="text-sm font-medium text-gray-900 mt-1">{parent.question_text}</div>
      {parent.big_picture_context && (
        <div className="text-xs text-gray-500 mt-1 italic">BP: {parent.big_picture_context}</div>
      )}
    </div>
  );
}

function AccommodationPicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2 mb-3" data-testid="variant-accom-picker">
      {ACCOMS.map((a) => {
        const Icon = a.icon;
        const active = value === a.code;
        return (
          <button
            key={a.code}
            type="button"
            onClick={() => onChange(a.code)}
            className={
              active
                ? `flex items-center gap-2 border-2 border-${a.color}-500 bg-${a.color}-50 text-${a.color}-900 px-3 py-2 text-sm font-bold`
                : "flex items-center gap-2 border border-gray-300 bg-white text-gray-700 px-3 py-2 text-sm hover:border-gray-400"
            }
            data-testid={`variant-accom-${a.code}`}
          >
            <Icon size={14} />
            <span>{a.code}</span>
            <span className="text-xs font-normal">{a.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function DraftEditor({ accommodation, draft, onChange }) {
  if (!draft) return null;

  if (accommodation === "RA") {
    return (
      <div className="space-y-2" data-testid="variant-draft-ra">
        <label className="text-xs font-bold uppercase tracking-wide text-gray-600">
          audio_script
        </label>
        <Textarea
          value={draft.audio_script || ""}
          onChange={(e) => onChange({ ...draft, audio_script: e.target.value })}
          rows={6}
          className="font-mono text-xs"
          data-testid="variant-draft-ra-script"
        />
        <label className="text-xs font-bold uppercase tracking-wide text-gray-600 mt-2 block">
          voice_notes
        </label>
        <Textarea
          value={draft.voice_notes || ""}
          onChange={(e) => onChange({ ...draft, voice_notes: e.target.value })}
          rows={3}
          className="font-mono text-xs"
          data-testid="variant-draft-ra-notes"
        />
        <label className="text-xs font-bold uppercase tracking-wide text-gray-600 mt-2 block">
          audio_url (paste recording URL after upload)
        </label>
        <Input
          value={draft.audio_url || ""}
          onChange={(e) => onChange({ ...draft, audio_url: e.target.value })}
          placeholder="https://... or /audio/..."
          className="font-mono text-xs"
          data-testid="variant-draft-ra-url"
        />
      </div>
    );
  }

  if (accommodation === "WD") {
    const langKeys = Object.keys(draft).filter((k) => k.startsWith("glossary_"));
    return (
      <div className="space-y-3" data-testid="variant-draft-wd">
        {langKeys.length === 0 && (
          <div className="text-xs text-gray-500 italic">
            No glossary fields in the draft yet. Make sure you selected at least one language before generating.
          </div>
        )}
        {langKeys.map((langKey) => {
          const lang = langKey.slice("glossary_".length);
          const entries = draft[langKey] || {};
          const langLabel = (WD_LANGS.find((l) => l.code === lang) || {}).label || lang;
          return (
            <div key={langKey} className="border border-gray-200 p-3">
              <div className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-2">
                {langKey} — {langLabel}
              </div>
              <div className="space-y-1">
                {Object.entries(entries).map(([word, gloss], idx) => (
                  <div key={`${langKey}-${idx}`} className="grid grid-cols-2 gap-2">
                    <Input
                      value={word}
                      onChange={(e) => {
                        const next = { ...entries };
                        delete next[word];
                        next[e.target.value] = gloss;
                        onChange({ ...draft, [langKey]: next });
                      }}
                      className="h-8 text-xs"
                      data-testid={`variant-draft-wd-${lang}-word-${idx}`}
                    />
                    <Input
                      value={gloss}
                      onChange={(e) => onChange({ ...draft, [langKey]: { ...entries, [word]: e.target.value } })}
                      className="h-8 text-xs"
                      data-testid={`variant-draft-wd-${lang}-gloss-${idx}`}
                    />
                  </div>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => onChange({ ...draft, [langKey]: { ...entries, "": "" } })}
                  data-testid={`variant-draft-wd-${lang}-add`}
                >
                  + Add entry
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ES + HL — UI-only.
  return (
    <div className="bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900" data-testid="variant-draft-uionly">
      This accommodation is a UI flag only — no content overrides needed. Click <strong>Save Variant</strong> to record an active variant so the student-facing toolbar lights up the correct widget.
    </div>
  );
}

// ---------------- main tab ----------------

export default function VariantAuthoringTab() {
  const [budget, setBudget] = useState(null);
  const [parent, setParent] = useState(null);
  const [accommodation, setAccommodation] = useState("RA");
  const [productLine, setProductLine] = useState("summative");
  const [glossaryLangs, setGlossaryLangs] = useState(["es"]);
  const [draft, setDraft] = useState(null);
  const [notes, setNotes] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [savedVariantId, setSavedVariantId] = useState(null);

  const fetchBudget = async () => {
    try {
      const res = await axios.get(`${API}/api/super-admin/variant-authoring/budget`);
      setBudget(res.data);
    } catch { /* noop */ }
  };
  useEffect(() => { fetchBudget(); }, []);

  const canGenerate = useMemo(() => {
    if (!parent) return false;
    if (accommodation === "WD" && glossaryLangs.length === 0) return false;
    return true;
  }, [parent, accommodation, glossaryLangs]);

  const generate = async () => {
    setGenLoading(true);
    setDraft(null);
    setSavedVariantId(null);
    try {
      const body = {
        parent_question_id: parent.id,
        accommodation,
        product_line: productLine,
      };
      if (accommodation === "WD") body.glossary_langs = glossaryLangs;
      const res = await axios.post(`${API}/api/super-admin/variant-authoring/generate`, body);
      setDraft(res.data.draft_overrides || {});
      setNotes(res.data.notes || "");
      if (res.data.parse_error) {
        toast.warning("MOSAIC content engine returned non-JSON; raw response preserved for inspection.");
      } else {
        toast.success(
          `Draft ready (est. $${(res.data.estimated_cost_usd || 0).toFixed(4)}).`
        );
      }
      fetchBudget();
    } catch (e) {
      toast.error("Generate failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setGenLoading(false);
    }
  };

  const save = async () => {
    setSaveLoading(true);
    try {
      const overrides = draft && Object.keys(draft).length > 0 ? draft : {};
      // Strip the _raw_response key if Claude returned non-JSON — operator
      // has been warned via toast; never persist parse-failures.
      delete overrides._raw_response;
      const body = {
        parent_question_id: parent.id,
        accommodation,
        product_line: productLine,
        overrides,
        notes: notes || `Authored via Variant Authoring Console (${accommodation})`,
      };
      const res = await axios.post(`${API}/api/super-admin/question-variants/`, body);
      const vid = res.data?.inserted?.variant_id;
      setSavedVariantId(vid);
      toast.success(`Variant ${vid} saved.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        toast.error("An active variant already exists for this combo. Retire it from the Question Variants admin first, then re-save.");
      } else {
        toast.error("Save failed: " + (detail || e.message));
      }
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="variant-authoring-root">
      <div className="bg-emerald-50 border-l-4 border-emerald-400 p-3">
        <div className="flex items-start gap-2">
          <Sparkles size={18} className="text-emerald-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-900">
            <p className="font-bold mb-1">Variant Authoring Console</p>
            <p>
              Generate accommodation variants for canonical questions via MOSAIC content engine.
              Drafts are returned to you for review and edit before any save. All saves go through
              the v6.32.151 append-only <code className="bg-emerald-100 px-1">question_variants</code> collection.
              {budget && (
                <span className="ml-1 font-mono">
                  Budget today: ${budget.spent_today_usd?.toFixed(4)} / ${budget.budget_usd?.toFixed(2)}.
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT — pick + configure */}
        <div className="space-y-3">
          {!parent ? (
            <QuestionPicker onPick={setParent} />
          ) : (
            <>
              <ParentPreview parent={parent} onClear={() => { setParent(null); setDraft(null); }} />
              <div className="bg-white border border-gray-200 p-4">
                <h3 className="text-sm font-bold text-gray-900 mb-3">Configure Variant</h3>
                <AccommodationPicker value={accommodation} onChange={(v) => { setAccommodation(v); setDraft(null); }} />

                <div className="mb-3">
                  <label className="text-xs font-bold uppercase tracking-wide text-gray-600 mb-1 block">Product line</label>
                  <select
                    value={productLine}
                    onChange={(e) => setProductLine(e.target.value)}
                    className="h-9 border border-gray-300 px-2 text-sm w-full"
                    data-testid="variant-product-line"
                  >
                    {PRODUCT_LINES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>

                {accommodation === "WD" && (
                  <div className="mb-3">
                    <label className="text-xs font-bold uppercase tracking-wide text-gray-600 mb-1 block">
                      Glossary languages
                    </label>
                    <div className="flex flex-wrap gap-1" data-testid="variant-wd-langs">
                      {WD_LANGS.map((l) => {
                        const on = glossaryLangs.includes(l.code);
                        return (
                          <button
                            key={l.code}
                            type="button"
                            onClick={() =>
                              setGlossaryLangs(
                                on
                                  ? glossaryLangs.filter((c) => c !== l.code)
                                  : [...glossaryLangs, l.code]
                              )
                            }
                            className={
                              on
                                ? "px-2 py-1 text-xs border-2 border-emerald-500 bg-emerald-50 text-emerald-900 font-bold"
                                : "px-2 py-1 text-xs border border-gray-300 bg-white text-gray-700"
                            }
                            data-testid={`variant-wd-lang-${l.code}`}
                          >
                            {l.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <Button
                  type="button"
                  onClick={generate}
                  disabled={!canGenerate || genLoading}
                  className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                  data-testid="variant-generate-btn"
                >
                  {genLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
                  {accommodation === "ES" || accommodation === "HL" ? "Prepare Variant" : "Generate Draft via MOSAIC content engine"}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* RIGHT — draft editor + save */}
        <div className="space-y-3">
          {!draft ? (
            <div className="bg-gray-50 border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500" data-testid="variant-draft-empty">
              Draft will appear here after you click <strong>Generate Draft</strong>.
            </div>
          ) : (
            <div className="bg-white border border-gray-200 p-4 space-y-3" data-testid="variant-draft-card">
              <h3 className="text-sm font-bold text-gray-900">
                Draft override ({accommodation} · {productLine})
              </h3>
              <DraftEditor accommodation={accommodation} draft={draft} onChange={setDraft} />

              <label className="text-xs font-bold uppercase tracking-wide text-gray-600 mt-3 block">
                Author notes (optional)
              </label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., reviewed for NYCPS Grade K rubric Aug 2026"
                className="text-xs"
                data-testid="variant-notes"
              />

              <Button
                type="button"
                onClick={save}
                disabled={saveLoading}
                className="h-10 bg-emerald-700 hover:bg-emerald-800 text-white w-full mt-2"
                data-testid="variant-save-btn"
              >
                {saveLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
                Save Variant
              </Button>

              {savedVariantId && (
                <div className="bg-emerald-50 border border-emerald-300 p-2 text-xs flex items-center gap-2" data-testid="variant-saved-receipt">
                  <CheckCircle size={14} className="text-emerald-700" />
                  <span>Saved as <code className="font-mono">{savedVariantId}</code></span>
                </div>
              )}
            </div>
          )}

          {draft && draft._raw_response && (
            <div className="bg-amber-50 border border-amber-300 p-3 text-xs" data-testid="variant-draft-rawresponse">
              <div className="font-bold text-amber-900 mb-1 flex items-center gap-1">
                <AlertTriangle size={14} /> MOSAIC content engine returned non-JSON
              </div>
              <pre className="font-mono text-amber-900 whitespace-pre-wrap text-[10px] max-h-40 overflow-y-auto">
                {draft._raw_response}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
